/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import { MaterialReactTable } from "material-react-table";
import { assetImages } from "constants";
import * as url from "helper/UrlHelper";
import { Link } from "react-router-dom";
import { getData } from "utils/Gateway";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import ViewTreatment from "components/ClinicalManagement/Treatments/Popup/ViewTreatment";
import SaveTreatment from "components/ClinicalManagement/Treatments/Popup/SaveTreatment";
import AskTreatmentConsent from "components/ClinicalManagement/Treatments/Popup/AskTreatmentConsent";

const treatmentStatusOptions = [
  { label: "Planned", value: "planned" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
  { label: "Paused", value: "paused" },
  { label: "Cancelled", value: "cancelled" },
];

// Helper function to map single value to option object
const mapValuesToSingleOption = (savedValue, optionsList) => {
  if (!savedValue) return null;

  const foundOption = optionsList.find((option) => option.value === savedValue);
  return foundOption || { value: savedValue, label: savedValue };
};

const TreatmentDetailsSection = ({
  patientObj = null,
  doctorObj = null,
  clinicObj = null,
}) => {
  const [rowSelection, setRowSelection] = useState({});
  const [treatmentList, setTreatmentList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [selectedViewTreatmentId, setSelectedViewTreatmentId] = useState(null);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
  const [selectedConsentTreatmentId, setSelectedConsentTreatmentId] =
    useState(null);

  const token = localStorage.getItem("token");

  // console.log("patientObj in TreatmentDetailsSection:", patientObj);

  const getAllTreatments = async () => {
    setRowSelection({});
    setIsLoading(true);

    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_TREATMENTS +
        `?token=${token}&modulename=treatment`;

      if (patientObj && patientObj._id) {
        requestUrl += `&patientid=${patientObj._id}`;
      }

      console.log("requestUrl:", requestUrl);

      const response = await getData(requestUrl);

      console.log("getAllTreatments response:", response);

      setIsLoading(false);

      if (response.status) {
        setTreatmentList(response.data);
        setShowAlert(true);
        setAlertMessage("Treatments fetched successfully");
        setMessageType("success");
      }
    } catch (error) {
      setIsLoading(false);
      setAlertMessage(error.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  useEffect(() => {
    getAllTreatments();
  }, [patientObj]);

  const columns = useMemo(
    () => [
      {
        header: "Treatment",
        accessorKey: "title",
      },
      {
        header: "Doctor",
        accessorKey: "medicalextension.doctor",
        Cell: ({ row }) => (
          <div className="owner border-bottom-0 d-flex align-items-center gap-1">
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.medicalextension?.doctor?.profileimage
                  ? url.SERVER_URL +
                    row.original.medicalextension.doctor.profileimage
                  : assetImages.defaultUser
              }
              alt="Doctor"
            />
            <p>
              <span className="d-block">
                {row.original.medicalextension?.doctor?.name || "N/A"}
              </span>
            </p>
          </div>
        ),
      },
      {
        header: "Start Date",
        accessorKey: "startdate",
        Cell: ({ row }) => (
          <span>
            {row.original.startdate
              ? new Date(row.original.startdate).toLocaleDateString("en-GB")
              : "N/A"}
          </span>
        ),
      },
      {
        header: "Status",
        accessorKey: "medicalextension.treatmentstatus",
        Cell: ({ row }) => {
          const treatmentStatus =
            row.original.medicalextension?.treatmentstatus;
          if (treatmentStatus) {
            const mappedStatus = mapValuesToSingleOption(
              treatmentStatus,
              treatmentStatusOptions
            );
            const statusColor =
              mappedStatus?.value === "completed"
                ? "text-success"
                : mappedStatus?.value === "ongoing"
                ? "text-primary"
                : mappedStatus?.value === "planned"
                ? "text-info"
                : mappedStatus?.value === "paused"
                ? "text-warning"
                : mappedStatus?.value === "cancelled"
                ? "text-danger"
                : "text-secondary";

            return (
              <p
                className={`d-flex align-items-center gap-1 ${statusColor} fw-semibold`}
              >
                <span className="d-block material-symbols-outlined icon-md">
                  check_circle
                </span>
                <span className="d-block">
                  {mappedStatus?.label || "UNKNOWN"}
                </span>
              </p>
            );
          }
          return (
            <p className="d-flex align-items-center gap-1 text-secondary fw-semibold">
              <span className="d-block material-symbols-outlined icon-md">
                help
              </span>
              <span className="d-block">NOT SET</span>
            </p>
          );
        },
      },
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <Link
            to="#"
            className="text-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#viewTreatmentOffCanvas"
            aria-controls="viewTreatmentOffCanvas"
            onClick={() => {
              setSelectedViewTreatmentId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined">
              visibility
            </span>
          </Link>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        accessorKey: "##",
        header: "Consent",
        Cell: ({ row }) => (
          <Link
            to="#"
            className="text-black"
            data-bs-toggle="offcanvas"
            data-bs-target="#askConsentTreatmentOffCanvas"
            aria-controls="askConsentTreatmentOffCanvas"
            onClick={() => {
              setSelectedConsentTreatmentId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined">lock</span>
          </Link>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
    ],
    []
  );

  const columnOrder = [
    "title",
    "medicalextension.doctor",
    "startdate",
    "medicalextension.treatmentstatus",
    "#",
    "consent",
  ];

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">
      <div className="filter-container mb-4">
        <div className="row align-items-center">
          <div className="col-lg-4">
            <h3 className="mb-3">Treatment Details</h3>
          </div>
          <div className="col-lg-8 d-flex align-items-center justify-content-end gap-1 gap-sm-2 gy-3 flex-wrap">
            <Link
              to="#"
              className="btn btn-gray"
              onClick={(e) => {
                e.preventDefault();
                getAllTreatments();
              }}
            >
              <span className="d-block material-symbols-outlined">refresh</span>
            </Link>
            <Link to="#" className="btn btn-gray">
              <span className="d-block material-symbols-outlined">tune</span>
            </Link>
            <Link
              to="#"
              className="btn btn-primary"
              data-bs-toggle="offcanvas"
              data-bs-target="#saveTreatmentTemplateOffCanvas"
              aria-controls="saveTreatmentTemplateOffCanvas"
              onClick={() => {
                setSelectedTreatmentId(null);
              }}
            >
              Add New
            </Link>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="placeholder-glow d-flex flex-column gap-4">
          <span className="placeholder placeholder-lg bg-secondary col-12"></span>
          <span className="placeholder placeholder-lg bg-secondary col-8"></span>
          <span className="placeholder placeholder-lg bg-secondary col-4"></span>
        </div>
      ) : (
        <div className="table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={treatmentList}
            enableGrouping
            enableRowSelection={false}
            getRowId={(row) => row._id}
            state={{ columnOrder }}
            initialState={{ columnOrder }}
            defaultColumn={{
              minSize: 20,
              maxSize: 300,
              size: 50,
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "60vh",
              },
            }}
            enableStickyHeader
          />
        </div>
      )}

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}

      <SaveTreatment
        selectedTreatmentId={selectedTreatmentId}
        setSelectedTreatmentId={setSelectedTreatmentId}
        afterActionClose={() => {
          getAllTreatments();
        }}
        setShowAlert={setShowAlert}
        setAlertMessage={setAlertMessage}
        setMessageType={setMessageType}
        defaultPatient={patientObj}
        defaultDoctor={doctorObj}
        defaultClinic={clinicObj}
      />

      <ViewTreatment
        selectedTreatmentId={selectedViewTreatmentId}
        setSelectedTreatmentId={setSelectedViewTreatmentId}
        afterActionClose={() => {
          getAllTreatments();
        }}
        setShowAlert={setShowAlert}
        setAlertMessage={setAlertMessage}
        setMessageType={setMessageType}
      />

      <AskTreatmentConsent
        selectedTreatmentId={selectedConsentTreatmentId}
        setSelectedTreatmentId={setSelectedConsentTreatmentId}
        // afterActionClose={() => {
        //   getAllTreatments();
        // }}
        setShowAlert={setShowAlert}
        setAlertMessage={setAlertMessage}
        setMessageType={setMessageType}
      />
    </div>
  );
};

export default TreatmentDetailsSection;
